import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Badge, Button, ProgressBar } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaChevronRight, FaBolt, FaInfoCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchUserPresences,
  getCurrentPresenceByUser,
} from "../../../redux/slices/presenceSlice";
import { getFullName } from "../../../utils/helpers";
import UserInitialsAvatar from "../../../utils/userInitialAvatar";

const PRESENCE_POLL_INTERVAL_MS = 30_000;
const TIME_UPDATE_INTERVAL_MS = 30_000;

const parseDate = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  let dateString = value;

  if (value.includes("T")) {
    const timePart = value.split("T")[1];
    if (!timePart.includes("Z") && !timePart.includes("+") && !timePart.includes("-")) {
      dateString = `${value}Z`;
    }
  }

  const date = new Date(dateString);

  return Number.isNaN(date.getTime()) ? null : date;
};

const isSameDay = (dateA, dateB = new Date()) => {
  if (!dateA) return false;

  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const getTimestamp = (value) => {
  const date = parseDate(value);
  return date ? date.getTime() : null;
};

const calculateWorkedMinutes = (presence) => {
  if (!presence?.check_in) {
    return 0;
  }

  const checkIn = getTimestamp(presence.check_in);

  if (checkIn === null) {
    return 0;
  }

  const checkOut = presence.check_out
    ? getTimestamp(presence.check_out)
    : Date.now();

  if (checkOut === null || checkOut <= checkIn) {
    return 0;
  }

  const shiftEnd = Math.max(checkIn, checkOut);

  const pauses = Array.isArray(presence.pauses) ? presence.pauses : [];

  let pausedMs = 0;

  pauses.forEach((pause) => {
    if (!pause?.start) return;

    const pauseStart = getTimestamp(pause.start);

    if (pauseStart === null) return;

    const pauseEnd = pause.end ? getTimestamp(pause.end) : Date.now();

    if (pauseEnd === null) return;

    const start = Math.max(pauseStart, checkIn);
    const end = Math.min(pauseEnd, shiftEnd);

    if (end > start) {
      pausedMs += end - start;
    }
  });

  const workedMs = Math.max(0, shiftEnd - checkIn - pausedMs);

  return Math.floor(workedMs / 60_000);
};

const formatMinutesAsTime = (minutes) => {
  const safeMinutes = Math.max(0, Math.floor(minutes || 0));

  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

const getSafeLocalDate = (value) => {
  if (!value || typeof value !== "string") {
    return "N/A";
  }

  const datePart = value.split("T")[0];
  const [year, month, day] = datePart.split("-");

  if (year?.length === 4 && month?.length === 2 && day?.length === 2) {
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString();
};

const OverviewTab = ({
  currentUser,
  timeStats,
  availableVacationDays,
  nextAbsence,
  capitalizedMonthName,
  formatMins,
  delegationName,
  balanceSummary,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentPresence = useSelector(
    (state) => state.presences?.currentPresence,
  );

  const presences = useSelector((state) => state.presences?.items || []);

  useEffect(() => {
    const userId = currentUser?.id;

    if (!userId) return;

    dispatch(fetchUserPresences(userId));
    dispatch(getCurrentPresenceByUser(userId));

    const interval = setInterval(() => {
      dispatch(getCurrentPresenceByUser(userId));
    }, PRESENCE_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch, currentUser?.id]);

  const activePresence = useMemo(() => {
  const today = new Date();
  const todayPresences = presences
    .filter((p) => {
      const isSameUser = String(p?.user_id) === String(currentUser.id);
      const checkInDate = parseDate(p?.check_in);
      return isSameUser && checkInDate && isSameDay(checkInDate, today);
    })
    .sort((a, b) => {
      const timeA = getTimestamp(a.check_in) || 0;
      const timeB = getTimestamp(b.check_in) || 0;
      return timeB - timeA;
    });
  const openPresenceInList = todayPresences.find((p) => !p?.check_out);
  if (openPresenceInList) {
    return openPresenceInList;
  }
  if (
    currentPresence?.check_in &&
    !currentPresence?.check_out &&
    String(currentPresence.user_id) === String(currentUser.id)
  ) {
    const isClosedInList = todayPresences.some(
      (p) => String(p.id) === String(currentPresence.id) && p.check_out
    );

    if (!isClosedInList) {
      const currentDate = parseDate(currentPresence.check_in);
      if (currentDate && isSameDay(currentDate, today)) {
        return currentPresence;
      }
    }
  }
  return todayPresences[0] || null;

}, [currentPresence, presences, currentUser.id]);

  const clockState = useMemo(() => {
    if (!activePresence?.check_in) {
      return "not_started";
    }

    const checkInDate = parseDate(activePresence.check_in);

    if (!checkInDate || !isSameDay(checkInDate)) {
      return "not_started";
    }

    const hasActivePause = Array.isArray(activePresence.pauses)
      ? activePresence.pauses.some((pause) => pause?.start && !pause?.end)
      : false;

    if (hasActivePause) {
      return "paused";
    }

    if (!activePresence.check_out) {
      return "working";
    }

    return "ended";
  }, [activePresence]);

  const [displayTime, setDisplayTime] = useState("00:00");

  useEffect(() => {
    const updateTime = () => {
      if (!activePresence?.check_in || clockState === "not_started") {
        setDisplayTime("00:00");
        return;
      }

      const workedMinutes = calculateWorkedMinutes(activePresence);

      setDisplayTime(formatMinutesAsTime(workedMinutes));
    };

    updateTime();

    const interval = setInterval(updateTime, TIME_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [activePresence, clockState]);

  const status = useMemo(() => {
    switch (clockState) {
      case "working":
        return {
          variant: "success",
          text: t("components.employeeProfile.overviewTab.active", {
            defaultValue: "Ativo",
          }),
        };

      case "paused":
        return {
          variant: "warning",
          text: t("components.employeeProfile.overviewTab.paused", {
            defaultValue: "Em Pausa",
          }),
        };

      case "ended":
        return {
          variant: "secondary",
          text: t("components.employeeProfile.overviewTab.inactive", {
            defaultValue: "Inativo",
          }),
        };

      default:
        return {
          variant: "secondary",
          text: t("components.employeeProfile.overviewTab.inactive", {
            defaultValue: "Inativo",
          }),
        };
    }
  }, [clockState, t]);

  const workedMinutes = Math.max(0, Number(timeStats?.worked) || 0);
  const expectedMinutes = Math.max(0, Number(timeStats?.expected) || 0);
  const remainingMinutes = Math.max(0, expectedMinutes - workedMinutes);

  const progressPercent =
    expectedMinutes > 0
      ? Math.min(100, Math.round((workedMinutes / expectedMinutes) * 100))
      : 0;

  const workedLabel = formatMins ? formatMins(workedMinutes) : "0h 00m";
  const balanceLabel = formatMins
    ? formatMins(Number(timeStats?.balance) || 0)
    : "+0h 00m";
  const remainingLabel = formatMins ? formatMins(remainingMinutes) : "0h 00m";
  const accumulatedBank = balanceSummary?.accumulated_bank || "+0h 00m";

  return (
    <Row className="g-4">
      <Col md={3} className="d-flex flex-column gap-4">
        <Card className="atlar-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.status.title", {
                  defaultValue: "Status",
                })}
              </h6>

              <Badge bg={status.variant} className="rounded-pill px-3 py-1">
                {status.text}
              </Badge>
            </div>

            <small className="txt-subtle d-block mb-1">
              {t("components.employeeProfile.overviewTab.timeToday", {
                defaultValue: "Tempo registado hoje",
              })}
            </small>

            <h2 className="fw-bold txt-main m-0" style={{ fontSize: "2.5rem" }}>
              {displayTime}
            </h2>
          </Card.Body>
        </Card>

        <Card className="atlar-card">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.expenses.title", {
                  defaultValue: "Despesas",
                })}
              </h6>

              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-pill fw-semibold border-2 px-3"
              >
                {t("components.employeeProfile.overviewTab.newExpense", {
                  defaultValue: "+ Novo",
                })}
              </Button>
            </div>

            <div
              className="d-flex justify-content-between txt-subtle mb-3"
              style={{ fontSize: "0.85rem" }}
            >
              <span>
                {t("components.employeeProfile.overviewTab.expenseStatus", {
                  defaultValue: "Estado",
                })}
              </span>

              <span>
                {t("components.employeeProfile.overviewTab.expenseAmount", {
                  defaultValue: "Despesas",
                })}
              </span>
            </div>

            {[
              ["text-danger", "expenseApproved", "Aprovado"],
              ["txt-subtle", "expenseSent", "Enviado"],
              ["text-success", "expenseReceipt", "Em recibo"],
              ["text-danger", "expensePaid", "Pago"],
              ["text-warning", "expensePending", "Pendente"],
            ].map(([colorClass, key, fallback]) => (
              <div
                key={key}
                className="d-flex justify-content-between align-items-center mb-2"
              >
                <span className={`fw-semibold ${colorClass}`}>
                  •{" "}
                  {t(`components.employeeProfile.overviewTab.${key}`, {
                    defaultValue: fallback,
                  })}
                </span>

                <span className="fw-bold txt-main">€ 0,00</span>
              </div>
            ))}
          </Card.Body>
        </Card>
      </Col>

      <Col md={3} className="d-flex flex-column gap-4">
        <Card className="atlar-card h-100">
          <Card.Body>
            <div className="d-flex align-items-center gap-1 mb-4">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.absences.title", {
                  defaultValue: "Ausências",
                })}
              </h6>

              <FaChevronRight className="txt-subtle" size={12} />
            </div>

            <small className="txt-subtle d-block mb-3">
              {t("components.employeeProfile.overviewTab.currentAndFuture", {
                defaultValue: "Atuais e futuras",
              })}
            </small>

            <div className="border rounded p-3 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-bold txt-main m-0 mb-1">
                  {t("components.employeeProfile.overviewTab.vacation", {
                    defaultValue: "Férias",
                  })}
                </h6>

                <small className="txt-subtle">
                  {availableVacationDays}{" "}
                  {t("components.employeeProfile.overviewTab.daysAvailable", {
                    defaultValue: "dias disponíveis",
                  })}
                </small>
              </div>

              <small
                className="txt-subtle text-end"
                style={{ maxWidth: "40%" }}
              >
                {nextAbsence?.start_date
                  ? parseDate(nextAbsence.start_date)?.toLocaleDateString() ||
                    t(
                      "components.employeeProfile.overviewTab.noFutureAbsences",
                      { defaultValue: "Sem ausências" },
                    )
                  : t(
                      "components.employeeProfile.overviewTab.noFutureAbsences",
                      { defaultValue: "Sem ausências" },
                    )}
              </small>
            </div>
          </Card.Body>
        </Card>

        <Card className="atlar-card h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-5">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.assets.title", {
                  defaultValue: "Equipamentos",
                })}
              </h6>

              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-pill fw-semibold border-2 px-3 d-flex align-items-center gap-2"
              >
                <FaBolt className="text-warning" />

                {t("components.employeeProfile.overviewTab.learnMore", {
                  defaultValue: "Saber mais",
                })}
              </Button>
            </div>

            <div className="text-center py-4">
              <div
                className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{
                  width: 40,
                  height: 40,
                }}
              >
                <FaInfoCircle size={20} />
              </div>

              <h6 className="fw-bold txt-main mb-1">
                {t("components.employeeProfile.overviewTab.noAssets", {
                  defaultValue: "Sem equipamentos",
                })}
              </h6>

              <small className="txt-subtle">
                {t("components.employeeProfile.overviewTab.assignAssets", {
                  defaultValue: "Atribuir ativos.",
                })}
              </small>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3} className="d-flex flex-column gap-4">
        <Card className="atlar-card">
          <Card.Body>
            <div className="mb-4">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.timesheet.title", {
                  defaultValue: "Folha de horas",
                })}

                <span className="txt-subtle fw-normal">
                  {" "}
                  • {capitalizedMonthName || "August"}
                </span>
              </h6>
            </div>

            <small className="txt-subtle d-block mb-1">
              {t("components.employeeProfile.overviewTab.workedPlanned", {
                defaultValue: "Horas trabalhadas / planeadas",
              })}
            </small>

            <div className="d-flex justify-content-between align-items-end mb-3">
              <h3 className="fw-bold txt-main m-0">{workedLabel}</h3>

              <span className="fw-bold text-success">{balanceLabel}</span>
            </div>

            <ProgressBar
              className="mb-3"
              style={{ height: "4px" }}
              now={progressPercent}
              variant="danger"
            />

            <div
              className="d-flex gap-3 mb-4 txt-subtle"
              style={{ fontSize: "0.85rem" }}
            >
              <span className="d-flex align-items-center gap-1">
                <span className="text-danger">•</span>

                {t("components.employeeProfile.overviewTab.worked", {
                  defaultValue: "Trabalhadas",
                })}
              </span>

              <span className="d-flex align-items-center gap-1">
                <span className="text-secondary">•</span>
                {remainingLabel}{" "}
                {t("components.employeeProfile.overviewTab.toComplete", {
                  defaultValue: "por completar",
                })}
              </span>
            </div>

            <hr className="bg-gray-200" />

            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="fw-semibold txt-main">
                {t("components.employeeProfile.overviewTab.accumulatedBank", {
                  defaultValue: "Banco de Horas:",
                })}
              </small>

              <small className="fw-bold text-success">{accumulatedBank}</small>
            </div>
          </Card.Body>
        </Card>

        <Card className="atlar-card">
          <Card.Body>
            <div className="d-flex align-items-center gap-1 mb-4">
              <h6 className="fw-bold txt-main m-0">
                {t("components.employeeProfile.overviewTab.tasks.title", {
                  defaultValue: "Tarefas",
                })}
              </h6>

              <FaChevronRight className="txt-subtle" size={12} />
            </div>

            <Row className="text-center mb-4">
              <Col>
                <h4 className="fw-bold text-danger m-0">0</h4>

                <small className="txt-subtle">
                  {t("components.employeeProfile.overviewTab.overdue", {
                    defaultValue: "Em atraso",
                  })}
                </small>
              </Col>

              <Col>
                <h4 className="fw-bold text-warning m-0">0</h4>

                <small className="txt-subtle">
                  {t("components.employeeProfile.overviewTab.pending", {
                    defaultValue: "Pendentes",
                  })}
                </small>
              </Col>

              <Col>
                <h4 className="fw-bold txt-main m-0">0</h4>

                <small className="txt-subtle">
                  {t("components.employeeProfile.overviewTab.total", {
                    defaultValue: "Total",
                  })}
                </small>
              </Col>
            </Row>

            <div className="text-center">
              <h6 className="fw-bold txt-main mb-1">
                {t("components.employeeProfile.overviewTab.noTasks", {
                  defaultValue: "Sem tarefas atribuídas",
                })}
              </h6>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Col md={3}>
        <Card className="atlar-card h-100">
          <Card.Body>
            <h6
              className="fw-bold txt-subtle mb-4 text-uppercase"
              style={{
                fontSize: "0.8rem",
                letterSpacing: "1px",
              }}
            >
              {t("components.employeeProfile.overviewTab.details.title", {
                defaultValue: "Detalhes",
              })}
            </h6>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.reportsTo", {
                  defaultValue: "Reporta a",
                })}
              </small>

              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  {currentUser?.manager ? (
                    <UserInitialsAvatar
                      user={currentUser.manager}
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "10px",
                      }}
                    />
                  ) : (
                    <div
                      className="bg-light text-secondary rounded d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: 24,
                        height: 24,
                        fontSize: "10px",
                      }}
                    >
                      ND
                    </div>
                  )}

                  <span className="fw-bold txt-main">
                    {currentUser?.manager
                      ? getFullName(currentUser.manager)
                      : t("components.employeeProfile.overviewTab.notDefined", {
                          defaultValue: "Não definido",
                        })}
                  </span>
                </div>

                <FaChevronRight className="txt-subtle" size={12} />
              </div>
            </div>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.email", {
                  defaultValue: "E-mail",
                })}
              </small>

              <span className="fw-bold txt-main">
                {currentUser?.email || "N/A"}
              </span>
            </div>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.phone", {
                  defaultValue: "Telefone",
                })}
              </small>

              <span className="fw-bold txt-main">
                {currentUser?.phone_number || "N/A"}
              </span>
            </div>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.legalEntity", {
                  defaultValue: "Entidade legal",
                })}
              </small>

              <span className="fw-bold txt-main">
                {delegationName || "N/A"}
              </span>
            </div>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.contractType", {
                  defaultValue: "Tipo de contrato",
                })}
              </small>

              <span className="fw-bold txt-main">
                {currentUser?.employment_type ||
                  t("components.employeeProfile.overviewTab.contract", {
                    defaultValue: "Contrato",
                  })}
              </span>
            </div>

            <div className="mb-4">
              <small className="txt-subtle d-block mb-1">
                {t("components.employeeProfile.overviewTab.hiringDate", {
                  defaultValue: "Data de contratação",
                })}
              </small>

              <span className="fw-bold txt-main">
                {getSafeLocalDate(currentUser?.hiring_date)}
              </span>
            </div>

            <div>
              <small className="txt-subtle d-block mb-2">
                {t("components.employeeProfile.overviewTab.workDays", {
                  defaultValue: "Dias de trabalho",
                })}
              </small>

              <div className="d-flex gap-2">
                {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
                  <div
                    key={`${day}-${index}`}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor:
                        index < 5 ? "#00D1D1" : "var(--gray-200)",
                      color: index < 5 ? "#fff" : "var(--gray-500)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default OverviewTab;